import { createSelector } from 'reselect';
import { getIdWoPrefix } from '../../utils';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty.js';
import moment from 'moment/src/moment.js';
import isEqual from 'lodash-es/isEqual.js';

import * as utils from './utils.js';

//Redux
import * as app from '../app';
import * as firestoreRedux from '@dreamworld/firestore-redux';
import * as router from '../router';
import * as auth from '../auth';
import { convertFirestoreTimestamp } from '../../utils.js';
import { accountId } from '../router/selectors.js';

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - subscription details for given/current account.
 */
export const subscription = createSelector(
  (state, accountId) => auth.selectors.currentUserId(state),
  (state, accountId) => firestoreRedux.selectors.doc(state, 'subscriptions', `sub_${getIdWoPrefix({ id: accountId || router.selectors.accountId(state), prefix: 'acc_' })}`),
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.subscription.data`),
  (userId, subscription, _subscription) => userId ? subscription: _subscription || {}
);

const subsriptionQueryStatus = (state, accountId) => firestoreRedux.selectors.queryStatus(state, `subscriptions.sub_${getIdWoPrefix({ id: accountId || router.selectors.accountId(state), prefix: 'acc_' })}`);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the subscription details API call.
 */
export const subscriptionStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.subscription.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - status of subscription of given/currently opened account.
 */
export const status = createSelector(
  (state, accountId) => subscription(state, accountId),
  (subscription) => subscription?.status
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } account currency.
 */
export const currency = createSelector(
  (state, accountId) => firestoreRedux.selectors.doc(state, 'accounts', accountId || router.selectors.accountId(state)),
  (state, accountId) => subsriptionQueryStatus(state, accountId),
  (attrs, currentPricing) => attrs?.currency || currentPricing?.currency || 'USD'
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { String } - Coupon code.
 */
export const coupon = createSelector(
  (state, accountId) => firestoreRedux.selectors.doc(state, 'accounts', accountId || router.selectors.accountId(state)),
  (attrs) => attrs?.coupon || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { Object } - Coupon details for given coupon code.
 *  - This is an actual response to the API call to get the coupon details.
 *  - e.g. { name, description }
 */
export const couponDetails = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.coupon.data`),
  (data) => {
    return data || {};
  }
);

export const _pricingData = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.pricing.data`),
  (data) => data || {}
);
/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - Pricing details for given/current account.
 *  - This is an actual response to the API call to get the pricing details. 
 *  - see response example: https://drive.google.com/file/d/1-DFWKIPV8iQRpb1Uh9fkmvTgLib222iZ/view 
 */
export const pricing = createSelector(
  (state, accountId) => _pricingData(state, accountId),
  (pricingDetails) => pricingDetails && pricingDetails.countryPrice || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the pricing details API call.
 */
export const pricingStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.pricing.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { Object } - Subscription pricing details for given/current account.
 */
export const subscriptionPricingDetails = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.subscriptionPricingDetails.data`),
  (pricingDetails) => {
    return pricingDetails || {};
  }
);

export const subscriptionPricingDetailsStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.subscriptionPricingDetails.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @returns {Object} current user's plan details.
 *  - @param {String} currencySymbol - Currency symbol.
 *  - @param {String} currency - Currency code.
 *  - @param {String} name - Plan name.
 *  - @param {Number} price - Price of the plan.
 *  - @param {String} interval - Interval (MONTHLY/YEARLY).
 *  - @param {String} stripePriceId - Stripe price ID.
 */
export const planDetails = createSelector(
  (state, accountId) => subscription(state, accountId),
  (state, accountId) => subsriptionQueryStatus(state, accountId),
  (state, accountId) => subscriptionPricingDetails(state, accountId),
  (state, accountId) => subscriptionPricingDetailsStatus(state, accountId),
  (state, accountId)  => currency(state, accountId),
  (subscription, subsriptionQueryStatus, pricing, status, accountCurrency) => {
    if ((isEmpty(subscription) && subsriptionQueryStatus === 'PENDING') || (subscription && subscription.stripePriceId && isEmpty(pricing) && status !== 'SUCCESS')) {
      return;
    }

    const priceId = subscription?.stripePriceId;
    const currency = pricing?.currency || accountCurrency || 'USD';
    const currencySymbol = utils.getCurrencySymbol(currency);

    if (!priceId) {
      return { plan: 'SOLO', currencySymbol, currency };
    }

    if(isEmpty(pricing)) {
      return { plan: 'SOLO', currencySymbol, currency };
    }
  
    return { currencySymbol, currency, name: utils.formatPlanName(pricing?.productName), price: pricing?.amount ? utils.getCurrencyAmount(pricing.amount, currency) : 0, interval: pricing?.interval, stripePriceId: pricing?.id };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { String } - Plan name for given/current account.
 */
export const plan = createSelector(
  (state, accountId) => planDetails(state, accountId),
  (planDetails) => {
    if(planDetails === undefined) {
      return;
    }
    return planDetails?.name || 'SOLO'
  }
);

/**
 * Available plans for given account.
 * @returns {Array} - List of available plans for given account.
 *  - @param {String} name - Plan name.
 *  - @param {Number} price - Price of the plan.
 *  - @param {String} currencySymbol - Currency symbol.
 *  - @param {String} currency - Currency code.
 *  - @param {String} stripePriceId - Stripe price ID.
 *  - @param {String} interval - Interval (monthly/yearly).
 */
export const availablePlans = createSelector(
  (state, accountId, interval) => pricing(state, accountId),
  (state, accountId, interval) => pricingStatus(state, accountId),
  (state, accountId, interval) => interval || 'MONTHLY',
  (state, accountId, interval) => enterprisePlanPlaceholderAvailable(state),
  (state, accountId, interval) => currency(state, accountId),
  (state, accountId, interval) => subscriptionPricingDetails(state, accountId),
  (pricing, status, interval, enterprisePlanPlaceholderAvailable, accountCurrency, currentPricing) => {
    // Early return if pricing data isn't loaded yet
    if (status !== 'SUCCESS') {
      return;
    }

    const currency = pricing?.currencyCode || accountCurrency || 'USD';
    const currencySymbol = utils.getCurrencySymbol(currency) ||  pricing?.currencySymbol || '$';
    const plans = [{ name: 'SOLO', currency, currencySymbol }]; // Always include SOLO plan

    // Process regular plans - extract to reduce nesting
    const regularPlans = processRegularPlans(pricing?.plans || {}, interval, currencySymbol, currency);
    plans.push(...regularPlans);
    
    // Process custom plans with optimized sorting
    const customPlans = processCustomPlans(pricing?.customPlans || {}, interval, currencySymbol, currency);
    plans.push(...customPlans);

    // Add current pricing if it exists and is not active
    if (currentPricing && currentPricing.id && currentPricing.interval === interval && (!currentPricing.productActive || !currentPricing.active)) {
      plans.push({
        name: utils.formatPlanName(currentPricing.productName),
        currencySymbol,
        price: currentPricing.amount ? utils.getCurrencyAmount(currentPricing.amount, currency, false) : 0,
        interval: currentPricing.interval,
        stripePriceId: currentPricing.id,
        currency,
        archived: true
      });
    }

    // Handle enterprise plan
    const enterprisePlan = processEnterprisePlan(
      pricing?.enterprisePlan,
      interval,
      currencySymbol,
      enterprisePlanPlaceholderAvailable,
      currency
    );
    
    if (enterprisePlan) {
      plans.push(enterprisePlan);
    }

    return plans;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

// Helper functions to improve readability and maintainability

/**
 * Process regular pricing plans
 * @param {Object} plans - The plans object from pricing data
 * @param {String} interval - Billing interval (MONTHLY/YEARLY)
 * @param {String} currencySymbol - Currency symbol to use
 * @param {String} currency - Currency code
 * @returns {Array} - Processed regular plans
 */
function processRegularPlans(plans, interval, currencySymbol, currency) {
  const result = [];
  
  for (const planName in plans) {
    const prices = plans[planName] || [];
    const price = prices.find(p => p.interval === interval);
    
    if (price) {
      result.push({
        name: utils.formatPlanName(planName),
        currencySymbol,
        price: price.price,
        stripePriceId: price.stripePriceId,
        interval: price.interval,
        isCustomPlan: false,
        currency
      });
    }
  }
  
  return result;
}

/**
 * Process custom pricing plans with sorting
 * @param {Object} customPlans - The custom plans object from pricing data
 * @param {String} interval - Billing interval (MONTHLY/YEARLY)
 * @param {String} currencySymbol - Currency symbol to use
 * @returns {Array} - Processed and sorted custom plans
 */
function processCustomPlans(customPlans, interval, currencySymbol, currency) {
  // Map to store plans by availability for efficient grouping
  const availabilityGroups = {
    PUBLIC: [],
    SELECTED_COUNTRIES: [],
    PRIVATE: []
  };
  
  // Process all custom plans first
  for (const planName in customPlans) {
    const planInfo = customPlans[planName];
    const planPrices = planInfo.plans || [];
    const price = planPrices.find(p => p.interval === interval);
    
    if (price) {
      // Determine the availability group, defaulting to PRIVATE
      const availability = planInfo.avaibility || 'PRIVATE';
      const group = availabilityGroups[availability] || availabilityGroups.PRIVATE;
      
      group.push({
        name: utils.formatPlanName(planName),
        currencySymbol,
        price: price.price,
        stripePriceId: price.stripePriceId,
        interval: price.interval,
        sortOrder: planInfo.sortOrder,
        availability,
        isCustomPlan: true,
        currency
      });
    }
  }
  
  // Custom sort function that puts items with missing sortOrder at the end
  const sortByOrder = (a, b) => {
    // If sortOrder is missing, null, or undefined in either object, place it at the end
    if (!('sortOrder' in a) || a.sortOrder === null || a.sortOrder === undefined) return 1;
    if (!('sortOrder' in b) || b.sortOrder === null || b.sortOrder === undefined) return -1;
    // Normal numeric sort for defined sortOrder values
    return a.sortOrder - b.sortOrder;
  };
  
  // Combine and sort all custom plans in the order of availability priority
  return [
    ...availabilityGroups.PUBLIC.sort(sortByOrder),
    ...availabilityGroups.SELECTED_COUNTRIES.sort(sortByOrder),
    ...availabilityGroups.PRIVATE.sort(sortByOrder)
  ];
}

/**
 * Process enterprise plan
 * @param {Object} enterprisePlan - The enterprise plan object from pricing data
 * @param {String} interval - Billing interval (MONTHLY/YEARLY)
 * @param {String} currencySymbol - Currency symbol to use
 * @param {Boolean} enterprisePlanPlaceholderAvailable - Whether enterprise plan is available
 * @returns {Object|null} - Processed enterprise plan or null
 */
function processEnterprisePlan(enterprisePlan, interval, currencySymbol, enterprisePlanPlaceholderAvailable, currency) {
  if (enterprisePlan) {
    const enterprisePrices = enterprisePlan.plans || [];
    const price = enterprisePrices.find(p => p.interval === interval);

    if (price) {
      return {
        name: utils.formatPlanName(enterprisePlan.productName),
        currencySymbol,
        price: price.price,
        stripePriceId: price.stripePriceId,
        interval: price.interval,
        isEnterprisePlan: true,
        currency
      };
    }
  } else if (enterprisePlanPlaceholderAvailable) {
    return { 
      name: 'ENTERPRISE', 
      currencySymbol,
      placeholder: true,
      isEnterprisePlan: true,
      currency
    };
  }

  return null;
}

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - charges for the subscription.
 *  - @param { Number } seats - Number of seats.
 *  - @param { Number } rate - Rate per seat in cent.
 *  - @param { Number } subTotal - sub-total amount in cent (seats * rate).
 *  - @param { Array } totalDiscountAmounts[] - Discount details.
 *    - @param { Number } amount - Discount amount in cent.
 *    - @param { String } name - Discount name.
 *    - @param { String } percentage - Discount percentage.
 *    - @param { String } description - Discount description.
 *    - @param { Number } amountOff - Discount amount off in cent.
 *  - @param { Number } totalExcludingTax - subtotal excluding tax amount in cent.
 *  - @param { Array } totalTaxAmounts[]
 *    - @param { Number } amount - Tax amount in cent.
 *    - @param { Number } taxableAmount - Taxable amount in cent.
 *    - @param { String } percentage - Tax percentage.
 *    - @param { String } name - Tax name.
 *    - @param { String } jurisdiction - jurisdiction (tax-applied city or state name)
 *  - @param { Number } total - Total amount in cent.
 *  - @param { Number } startingBalance - customer starting balance in cent.
 *  - @param { Number } endingBalance - customer ending balance in cent.
 *  - @param { Number } appliedBalance - applied balance in cent.
 *  - @param { Number } amountDue - Amount due in cent.
 *  - @param { String } expiry - Expiry date of the new subsription.
 */
export const charges = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.charges.data`),
  (charges) => charges || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the charges computation API call.
 */
export const chargesStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.charges.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - charges computation process is failed due to this error.
 */
export const chargesError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.charges.error`),
  (error) => error || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { String } - Promotion code 
 */
export const promotionCode = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.charges.promotionCode`),
  (promotionCode) => promotionCode || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - start subsription process details.
 *  - @param { Number } seats - Number of seats.
 *  - @param { String } priceId - Pricing ID.
 *  - @param { String } interval - Interval (monthly/yearly).
 *  - @param { Boolean } autoRenewal - Auto-renewal flag.
 *  - @param { String } accountId - Account ID.
 *  - @param { Enum } plan - Plan name.
 */
export const changePlan = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.changePlan.data`),
  (data) => data || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the change plan. it can be 'IN_PROGRESS', 'SUCCESS', 'ERROR'.
 */
export const changePlanStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.changePlan.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - change plan process is failed due to this error.
 *                   - POSSIBLE VALUES: 'ALREADY_SUBSCRIBED', 'UNAUTHORIZED','ACCOUNT_NOT_FOUND', 'UNKNOWN', 'PRICE_NOT_MATCH_WITH_COUNTRY'
 */
export const changePlanError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.changePlan.error`),
  (error) => error || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - billing details for given/current account.
 *  e.g. { "name": "shakil mansuri", "phone": "+917405567017", "organization": null, "address": { "line1": "Near primay school", "line2": "Panchayat Street", "city": "Bharuch", "state": "GJ", "postalCode": "392012", "country": "IN"}, "billingEmails": [], "localTax": { "type": null, "value": null } }
 */
export const billingDetails = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.billingDetails.data`),
  (billingDetails) => billingDetails || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the billing details API call.
 */
export const billingDetailsStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.billingDetails.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the change auto renew process.
 */
export const changeAutoRenewStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.changeAutoRenew.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - change auto renew process is failed due to this error.
 */
export const changeAutoRenewError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.changeAutoRenew.error`),
  (error) => error || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the setup payment method process.
 */
export const setupPaymentMethodStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.setupPaymentMethod.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - setup payment method process is failed due to this error.
 */
export const setupPaymentMethodError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.setupPaymentMethod.error`),
  (error) => error || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Boolean } - setup payment method process is in same window.
 */
export const setupPaymentMethodInSameWindow = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.setupPaymentMethod.inSameWindow`),
  (inSameWindow) => inSameWindow || false
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Array } - List of invoices for given/current account.
 *  - @param { String } id - Invoice ID.
 *  - @param { String } number - Invoice number.
 *  - @param { Enum } status - Invoice status. Possible values: 'PAID', 'CLOSED', 'FAILED', 'FAILED_RETRING', 'OVERDUE', 'PENDING'
 *  - @param { Enum } stripeStatus - Stripe status. Possible values: 'paid', 'pending', 'failed', 'overdue', 'closed', 'failed_retring'.
 *  - @param { Number } amountDue - Amount due in cent.
 *  - @param { Number } total - Total amount in cent.
 *  - @param { String } currency - Currency of the invoice.
 *  - @param { Timestamp } createdAt - Invoice creation date.
 *  - @param { Timestamp } dueDate - Invoice due date.
 *  - @param { Timestamp } paidAt - Invoice paid date.
 *  - @param { Object } paymentMethodDetails - Payment details.
 *    - @param { String } type - Payment method type.
 *    - @param { String } displayTemplate - Display template for payment method.
 *  - @param { Object } failure - Failure details.
 *    - @param { String } code - Failure code.
 *    - @param { String } message - Failure message.
 *  - @param { Timestamp } nextPaymentAttempt - Next payment attempt date.
 *  - @param { Number } attemptCount - Number of payment attempts.
 *  - @param { String } notes - Invoice notes.
 *  - @param { String } invoicePdfUrl - Invoice PDF URL.
 *  - @param { String } receiptPdfUrl - Receipt PDF URL.
 *  - @param { String } hostedInvoiceUrl - Hosted invoice
 */
export const invoices = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.invoices.data.invoices`),
  (state, accountId) => invoiceCharges(state, accountId),
  (invoices, charges) => {
    if (!invoices || invoices.length === 0) {
      return [];
    }

    return invoices.map(invoice => {
      const dueDate = convertFirestoreTimestamp(invoice.due_date || 0);
      // Notes > paid and paid_out_of_band are removes on Stripe new version, but still used here for backward compatibility.
      const isPaid = invoice.status === 'paid' || invoice.paid || invoice.paid_out_of_band || false;
      const isClosed = invoice.status === 'void' || invoice.status === 'uncollectible' || false;
      const isPastDue = invoice.status === 'open' && dueDate && dueDate < Date.now() || false;
      const isRetrying = invoice.status === 'open' && invoice.attempted && !!invoice.attempt_count && !!invoice.next_payment_attempt || false;
      const isFailed = invoice.status === 'open' && invoice.attempted && !!invoice.attempt_count && !invoice.next_payment_attempt || false;

      const status = isPaid ? 'PAID' : 
                    isClosed ? 'CLOSED' : 
                    isRetrying ? 'FAILED_RETRING' :
                    isFailed ? 'FAILED' :
                    isPastDue ? 'OVERDUE' : 'PENDING';

      const paidAt = convertFirestoreTimestamp(invoice?.status_transitions?.paid_at || 
                    invoice?.status_transitions?.voided_at || 
                    invoice?.status_transitions?.marked_uncollectible_at || 0);

      const createdAt = convertFirestoreTimestamp(invoice.effective_at || 
                        invoice.automatically_finalizes_at || 
                        invoice?.status_transitions?.finalized_at || 
                        invoice.created || 
                        0);
      // Extract payment method details from invoice for UI display
      const paymentMethodDetails = {};
      
      // Set basic payment type
      // Extract payment method details from charge
      const payment = invoice?.payments?.data?.[0]?.payment || {};
      const paymentType = payment?.type || '';

      let charge;
      if (paymentType === 'charge') {
        charge = charges[payment.charge] || {};
      } else if (paymentType === 'payment_intent') {
        // Find charge where payment_intent matches
        const paymentIntentId = payment.payment_intent;
        // Look through charges to find matching payment_intent
        charge = Object.values(charges).find(c => c?.payment_intent === paymentIntentId) || {};
      } else {
        charge = (invoice?.charge && typeof invoice.charge === 'object') ? invoice.charge : 
             (payment?.charge && typeof payment.charge === 'object') ? payment.charge : {};
      }

      if (charge?.payment_method_details?.type) {
        const details = charge.payment_method_details;
        paymentMethodDetails.type = details.type;
        
        // Define display templates for different payment types
        const displayTemplates = {
          card: details => `${details.card.brand || 'Card'} - ${details.card.last4 || '****'} ${details.card.exp_month || 'MM'}/${details.card.exp_year || 'YY'}`,
          ach_credit_transfer: details => `${details.ach_credit_transfer.bank_name || 'Bank'} - ${details.ach_credit_transfer.account_number || '****'}`,
          ach_debit: details => `${details.ach_debit.bank_name || 'Bank'} - ${details.ach_debit.last4 || '****'}`,
          acss_debit: details => `${details.acss_debit.bank_name || 'Bank'} - ${details.acss_debit.last4 || '****'}`,
          us_bank_account: details => `${details.us_bank_account.bank_name || 'Bank'} - ${details.us_bank_account.last4 || '****'}`,
          bank_transfer: details => `${details.bank_transfer.bank_name || 'Bank'} - Ref: ${details.bank_transfer.reference || ''}`,
          sepa_debit: details => `SEPA - ${details.sepa_debit?.last4 || '****'}`,
          sofort: details => `Sofort - ${details.sofort?.country || ''}`,
          ideal: details => `iDEAL - ${details.ideal?.bank || 'Bank'}`,
          giropay: details => `Giropay - ${details.giropay?.bank_code || ''}`,
          bancontact: details => `Bancontact - ${details.bancontact?.bank_code || ''}`,
          amazon_pay: details => `Amazon Pay${details.amazon_pay?.type ? ` - ${details.amazon_pay.type}` : ''}`,
          samsung_pay: details => `Samsung Pay${details.samsung_pay?.buyer_id ? ` - ${details.samsung_pay.buyer_id}` : ''}`,
          apple_pay: details => `Apple Pay${details.apple_pay?.transaction_id ? ` - ${details.apple_pay.transaction_id.substring(0, 8)}` : ''}`,
          google_pay: details => `Google Pay`
        };
        
        // Try to get the display template for this payment type
        if (displayTemplates[details.type] && details[details.type]) {
          try {
            paymentMethodDetails.displayTemplate = displayTemplates[details.type](details);
          } catch (e) {
            paymentMethodDetails.displayTemplate = details.type.replace(/_/g, ' ');
          }
        } else {
          paymentMethodDetails.displayTemplate = details.type.replace(/_/g, ' ');
        }
      }

      const failure = {
        code: charge?.failure_code || '',
        message: charge?.failure_message || ''
      }

      // Calculate applied balance with proper fallback
      const startingBalance = invoice.starting_balance || 0;
      const endingBalance = invoice.ending_balance || 0;
      const appliedBalance = startingBalance ? startingBalance - endingBalance : 0;

      return {
        id: invoice.id,
        number: invoice.number,
        status,
        stripeStatus: invoice.status,
        amountDue: invoice.amount_due,
        total: invoice.total,
        currency: invoice.currency || 'usd',
        createdAt,
        dueDate,
        paidAt,
        paymentMethodDetails,
        failure,
        nextPaymentAttempt: invoice.next_payment_attempt,
        notes: invoice.notes,
        invoicePdfUrl: invoice.invoice_pdf,
        receiptPdfUrl: charge?.receipt_url,
        hostedInvoiceUrl: invoice.hosted_invoice_url,
        attemptCount: invoice.attempt_count,
        appliedBalance
      };
    }).sort((a, b) => {
      // Enhanced sorting: ensure we handle all cases even if timestamps are missing
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA; // Newest first
    });
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);


const taxRates = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.invoices.data.taxRates`),
  (taxRates) => {
    if (isEmpty(taxRates)) {
      return {};
    }

    return taxRates.reduce((map, rate) => {
      if (rate?.id) {
        map[rate.id] = rate;
      }
      return map;
    }, {});
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

const invoiceCharges = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.invoices.data.charges`),
  (charges) => {
    if (isEmpty(charges)) {
      return {};
    }

    return charges.reduce((map, rate) => {
      if (rate?.id) {
        map[rate.id] = rate;
      }
      return map;
    }, {});
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - Upcoming invoice details for given/current account.
 *  - @param { String } id - Invoice ID.
 *  - @param { Number } createdAt - Invoice creation date.
 *  - @param { String } currency - Invoice currency.
 *  - @param { Array } lines[] - Invoice line items.
 *    - @param { String } description - Line item description.
 *    - @param { Number } quantity - Line item quantity.
 *    - @param { String } currency - Line item currency.
 *    - @param { Number } unitAmount - Line item unit amount in cent.
 *    - @param { Number } amount - Line item amount in cent.
 *    - @param { String } plan - Line item plan name.
 *  - @param { Number } subTotal - Sub-total amount in cent.
 *  - @param { Array } totalDiscountAmounts[] - Discount details.
 *    - @param { Number } amount - Discount amount in cent.
 *    - @param { String } name - Discount name.
 *    - @param { String } percentage - Discount percentage.
 *    - @param { String } description - Discount description.
 *    - @param { Number } amountOff - Discount amount off in cent.
 *  - @param { Number } totalExcludingTax - Sub-total excluding tax amount in cent.
 *  - @param { Array } totalTaxAmounts[] - Tax details.
 *    - @param { Number } amount - Tax amount in cent.
 *    - @param { Number } taxableAmount - Taxable amount in cent.
 *    - @param { String } percentage - Tax percentage.
 *    - @param { String } name - Tax name.
 *    - @param { String } jurisdiction - Tax jurisdiction.
 *  - @param { Number } total - Total amount in cent.
 *  - @param { Number } appliedBalance - Applied balance in cent.
 *  - @param { Number } amountDue - Amount due in cent.
 */
export const upcomingInvoice = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.invoices.data.upcomingInvoice`),
  (state, accountId) => taxRates(state, accountId),
  (upcomingInvoice, taxRates) => {
    if (!upcomingInvoice || isEmpty(upcomingInvoice)) {
      return {};
    }

    const lines = upcomingInvoice?.lines?.data || [];
    const _newLines = lines.map(line => ({
      id: line.id || '',
      description: line.description || '',
      quantity: line.quantity || 0,
      currency: line.currency || 'usd',
      //Notes > unit_amount and unit_amount_excluding_tax are removes on Stripe new version, but still used here for backward compatibility.
      unitAmount: line.unit_amount_decimal || line.unit_amount_excluding_tax || line.unit_amount || line?.pricing?.unit_amount_decimal || 0,
      amount: line.amount || 0,
    })); 

    const totalDiscountAmounts = upcomingInvoice?.total_discount_amounts || [];
    const discountsDetails = upcomingInvoice?.discounts || [];
    const discountsById = discountsDetails.reduce((map, d) => {
      if (d?.id) map[d.id] = d;
      if (d?.discount) map[d.discount] = d;
      return map;
    }, {});

    const _newTotalDiscountAmounts = totalDiscountAmounts.map(d => {
      const discount = d?.discount;
      const id = d?.id || (discount && typeof discount === 'object' ? discount.id : null);
      const detail = discountsById[id] || (discount && typeof discount === 'object' ? discount : {});
      const coupon = get(detail, 'source.coupon', {});

      return {
        id,
        amount: d?.amount || 0,
        name: get(coupon, 'name', '') || get(detail, 'name', ''),
        percentage: get(coupon, 'percent_off', '') || get(detail, 'percent_off', ''),
        description: get(coupon, 'metadata.description', '') || get(coupon, 'description', '') || get(detail, 'description', ''),
        amountOff: get(coupon, 'amount_off', 0) || get(detail, 'amount_off', 0)
      };
    });

    // Notes > total_tax_amounts is removes on Stripe new version, but still used here for backward compatibility.
    const totalTaxAmounts = upcomingInvoice?.total_tax_amounts || upcomingInvoice?.total_taxes || [];

    const _newTotalTaxAmounts = totalTaxAmounts.map(tax => {
      const directTaxRate = tax?.tax_rate;
      const taxRateId = tax?.tax_rate_details?.tax_rate;
      const taxRate = (!isEmpty(directTaxRate) ? directTaxRate : 
              (taxRateId && taxRates[taxRateId])) || {};

      return {
        amount: tax.amount || 0,
        taxableAmount: tax.taxable_amount || 0,
        percentage: get(taxRate, 'effective_percentage', ''),
        id: get(taxRate, 'id', ''),
        name: get(taxRate, 'display_name', ''),
        jurisdiction: get(taxRate, 'jurisdiction', '')
      };
    });

    // Calculate applied balance with proper fallback
    const startingBalance = upcomingInvoice.starting_balance || 0;
    const endingBalance = upcomingInvoice.ending_balance || 0;
    const appliedBalance = startingBalance ? startingBalance - endingBalance : 0;

    const createdAt = convertFirestoreTimestamp(upcomingInvoice.effective_at || upcomingInvoice.automatically_finalizes_at || upcomingInvoice?.status_transitions?.finalized_at || upcomingInvoice.created || 0);

    return {
      id: upcomingInvoice.id || '',
      createdAt,
      lines: _newLines,
      currency: upcomingInvoice.currency || 'usd',
      subTotal: upcomingInvoice.subtotal || 0,
      totalDiscountAmounts: _newTotalDiscountAmounts,
      totalExcludingTax: upcomingInvoice.total_excluding_tax || upcomingInvoice.subtotal || 0,
      totalTaxAmounts: _newTotalTaxAmounts,
      total: upcomingInvoice.total || 0,
      appliedBalance,
      amountDue: upcomingInvoice.amount_due || 0,
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - Invoice details for given invoice.
 */
export const invoice = createSelector(
  (state, accountId, invoiceId) => invoices(state, accountId).find(invoice => invoice.id === invoiceId),
  (invoice) => invoice || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the invoices API call.
 */
export const invoicesStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.invoices.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Number } - Balance of the customer.
 */
export const customerBalance = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.customerBalance.data.balance`),
  (balance) => balance || 0
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the customer balance API call.
 */
export const customerBalanceStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.customerBalance.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Array } - List of customer balance transactions.
 *  - @param { String } id - Transaction ID.
 *  - @param { Timestamp } createdAt - Transaction creation date.
 *  - @param { Enum } type - Transaction type. Possible values: adjustment, applied_to_invoice, credit_note, initial, invoice_overpaid, invoice_too_large, invoice_too_small, unspent_receiver_credit, or unapplied_from_invoice
 *  - @param { Number } amount - Transaction amount in cent.
 *  - @param { String } notes - Transaction description.
 *  - @param { String } currency - Currency of the transaction.
 *  - @param { Number } endingBalance - Ending balance after transaction in cent.
 *  - @param { Object } invoice - Invoice details.
 *    - @param { String } id - Invoice ID.
 *    - @param { String } number - Invoice number.
 *    - @param { String } invoicePdfUrl - Invoice PDF URL.
 */
export const customerBalanceTransactions = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.customerBalance.data.transactions`),
  (items) => {
    if (!items || items.length === 0) {
      return [];
    }

    return items.map(item => {
      return {
        id: item.id,
        type: item.type,
        createdAt: convertFirestoreTimestamp(item.created || 0),
        amount: item.amount,
        currency: item.currency,
        endingBalance: item.ending_balance,
        notes: item.description,
        invoice: {
          id: item?.invoice?.id || '',
          number: item?.invoice?.number || '',
          invoicePdfUrl: item?.invoice?.invoice_pdf || ''
        }
      };
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Number } - Number of pending invoices.
 * - It's a count of pending invoices.
 */
export const pendingInvoiceCount = createSelector(
  (state, accountId) => firestoreRedux.selectors.doc(state, 'accounts', accountId || router.selectors.accountId(state)),
  (attrs) => attrs?.pendingInvoiceCount || 0
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Array } - Array of pending invoice amounts grouped by currency.
 * Each array item contains { amount, currency, count }
 */
export const pendingInvoicesAmount = createSelector(
  (state, accountId) => invoices(state, accountId),
  (invoices) => {
    // Early return for empty case
    if (!invoices || invoices.length === 0) {
      return [];
    }
    
    const result = [];
    const currencyMap = {};
    
    for (let i = 0; i < invoices.length; i++) {
      const invoice = invoices[i];
      if (invoice.stripeStatus === 'open') {
        const currency = invoice.currency || 'usd';
        
        if (currencyMap[currency] === undefined) {
          currencyMap[currency] = result.length;
          result.push({
            amount: invoice.amountDue,
            count: 1,
            currency
          });
        } else {
          const index = currencyMap[currency];
          result[index].amount += invoice.amountDue;
          result[index].count += 1;
        }
      }
    }
    
    return result;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Array } - List of subscription history items.
 *  - @param { String } id - Item ID.
 *  - @param { String } accountId - Account ID.
 *  - @param { String } subscriptionId - Subscription ID.
 *  - @param { Enum } type - Item type. Possible values: AUTO_RENEW_CHANGED, PLAN_CHANGED, BILLING_CONTACTS_CHANGED, SUBSCRIBED, CANCELED, SEATS_CHANGED
 *  - @param { String } actionBy - Action performed by userId
 *  - @param { Timestamp } actionAt - Action performed at.
 *  - @param { Object } detail - Item detail. [see](https://git.kerika.net/kerikav4/subscription-server/-/blob/master/docs/models.md#itemdetail)
 */
const subscriptionsHistoryItems = createSelector(
  (state, accountId) => firestoreRedux.selectors.docsByQueryResult(state, `subscription-history-items_accountId-${accountId || router.selectors.accountId(state)}`, 'subscription-history-items'),
  (items) => {
    if (!items || items.length === 0) {
      return [];
    }

    return items.map(item => {
      return {
        id: item.id,
        accountId: item.accountId,
        subscriptionId: item.subscriptionId,
        type: item.type,
        actionBy: item.actionBy,
        actionAt: item.actionAt || 0,
        detail: item.detail
      };
    }).sort((a, b) => new Date(b.actionAt) - new Date(a.actionAt));
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the subscription history items API call.
 */
const subscriptionsHistoryItemsStatus = createSelector(
  (state, accountId) => firestoreRedux.selectors.queryStatus(state, `subscription-history-items_accountId-${accountId || router.selectors.accountId(state)}`),
  (status) => status || 'PENDING'
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { Array } - List of subscription history items merged with invoices and sorting on date.
 *  e.g.  [
            {
              id: "in_1NxYZaABCDEF123456",
              number: "INV-0001",
              status: "PAID",
              stripeStatus: "paid",
              amount_due: 10000, // $100.00 in cents
              total: 10000,
              currency: "usd",
              createdAt: 1672531200000, // Jan 1, 2023
              paymentMethodDetails: {
                type: "card",
                displayTemplate: "visa - 4242 12/24"
              },
              itemType: "INVOICE",
              timestamp: 1672531200000
            },
            {
              itemType: "HISTORY",
              timestamp: 1671926400000, // Dec 25, 2022
              date: "2022-12-25",
              actionAt: 1671926400000,
              items: [
                {
                  id: "hist_123",
                  accountId: "acc_123456",
                  subscriptionId: "sub_1234",
                  type: "PLAN_CHANGED",
                  actionBy: "user_123",
                  actionAt: 1671926400000, // Dec 25, 2022
                  detail: {
                  }
                },
                {
                  id: "hist_124",
                  accountId: "acc_123456",
                  subscriptionId: "sub_1234",
                  type: "SEATS_CHANGED",
                  actionBy: "user_123",
                  actionAt: 1671925200000, // Also Dec 25, 2022, but slightly earlier
                  detail: {
                  }
                }
              ]
            },
            {
              id: "in_1NxYZaABCDEF789012",
              number: "INV-0002",
              status: "PAID",
              stripeStatus: "paid",
              amount_due: 5000, // $50.00 in cents
              total: 5000,
              currency: "usd",
              createdAt: 1669852800000, // Dec 1, 2022
              paymentMethodDetails: {
                type: "card",
                displayTemplate: "visa - 4242 12/24"
              },
              itemType: "INVOICE",
              timestamp: 1669852800000
            }
          ]
 */
export const billingHistory = createSelector(
  (state, accountId) => invoices(state, accountId),
  (state, accountId) => subscriptionsHistoryItems(state, accountId),
  (invoices, historyItems) => {
    if (!invoices?.length && !historyItems?.length) {
      return [];
    }
    
    // Process invoices to match format for merging
    const processedInvoices = invoices.map(invoice => ({
      ...invoice,
      itemType: 'INVOICE',
      // Use invoice's createdAt as the sorting timestamp
      timestamp: invoice.createdAt || 0
    }));
    
    // Process history items to match format for merging
    const processedHistoryItems = historyItems.map(item => ({
      ...item,
      itemType: 'HISTORY',
      // Use history item's actionAt as the sorting timestamp
      timestamp: item.actionAt || 0
    }));
    
    // First, combine all items and sort by timestamp
    const allItems = [...processedInvoices, ...processedHistoryItems]
      .sort((a, b) => b.timestamp - a.timestamp);

    // Now group same-day history items
    const result = [];
    const groupedByDate = {};
    
    allItems.forEach(item => {
      if (item.itemType === 'INVOICE') {
        // Keep invoices separate, don't group them
        result.push(item);
      } else {
        // For history items, we need to group them between invoices
        if (!result.length) {
          // If no items yet, start with this history item
          const historyGroup = {
            itemType: 'HISTORY',
            timestamp: item.timestamp,
            date: moment(item.timestamp).format('YYYY-MM-DD'),
            items: [item],
            actionAt: item.actionAt
          };
          
          result.push(historyGroup);
        } else {
          // Check if the last item is an invoice or a history group
          const lastItem = result[result.length - 1];
          
          if (lastItem.itemType === 'HISTORY') {
            // Add to existing history group
            lastItem.items.push(item);
            // Update timestamp if this one is more recent
            if (item.timestamp > lastItem.timestamp) {
              lastItem.timestamp = item.timestamp;
              lastItem.actionAt = item.actionAt;
            }
          } else {
            // Last item was an invoice, create a new history group
            const historyGroup = {
              itemType: 'HISTORY',
              timestamp: item.timestamp,
              date: moment(item.timestamp).format('YYYY-MM-DD'),
              items: [item],
              actionAt: item.actionAt
            };
            
            result.push(historyGroup);
          }
        }
      }
    });
    
    // Add the grouped history items to results
    Object.values(groupedByDate).forEach(group => {
      result.push(group);
    });
    
    // Final sort to ensure everything is in correct order
    return result.sort((a, b) => b.timestamp - a.timestamp);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 10,
    },
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the billing history API call.
 */
export const billingHistoryStatus = createSelector(
  (state, accountId) => invoicesStatus(state, accountId),
  (state, accountId) => subscriptionsHistoryItemsStatus(state, accountId),
  (invoicesStatus, historyItemsStatus) => invoicesStatus === 'IN_PROGRESS' || historyItemsStatus === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'SUCCESS'
);

/**
 * @param {Object} state Redux state
 * @returns {Boolean} current user emill is from public domains or not.
 */
export const publicDomain = (state) => get(state, `subscription-v2.publicDomain.value`, false);

/**
 * @param {Object} state Redux state
 * @returns {Enum} public domain status. possible values: 'IN_PROGRESS', 'SUCCESS', 'ERROR'
 */
export const publicDomainStatus = (state) => get(state, `subscription-v2.publicDomain.status`, '');

// ================================== START: Subscription Messages ==================================
export const messageKnown = (state, accountId, name) => {
  const message = get(state, `subscription-v2.messages.${accountId || router.selectors.accountId(state)}.${name}`, {});
  return get(message, 'known', false) || false;;
};

export const messageKnownAt = (state, accountId, name) => {
  const message = get(state, `subscription-v2.messages.${accountId || router.selectors.accountId(state)}.${name}`, {});
  return get(message, 'knownAt', 0) || 0;
};

export const trilEndsDays = (state) => {
  const _subscription = subscription(state);
  return utils.calculateDaysRemaining(_subscription?.trialEnds);
}

export const expireDays = (state) => {
  const _subscription = subscription(state);
  return utils.calculateDaysRemaining(_subscription?.expirationDate);
}

const autoRenewOffMessageAvailable = (state) => {
  const _status = status(state);
  if(!_status) {
    return false;
  }

  if(_status !== 'ACTIVE') {
    return false;
  }

  const _subscription = subscription(state);
  const _autoRenew = _subscription.autoRenew;
  if(_autoRenew) {
    return false;
  }

  const _expireDays = expireDays(state);
  const config = app.selectors.config(state);
  const autoRenewOffNotificationBeforeDays = config?.autoRenewOffNotificationBeforeDays;
  if(_expireDays > autoRenewOffNotificationBeforeDays) {
    return false;
  }

  const _cancelOnExpire = _subscription.cancelOnExpire;
  if(_cancelOnExpire) {
    return false;
  }

  const _messageKnown = messageKnown(state, router.selectors.accountId(state), 'AUTO_RENEW_OFF');
  if(!_messageKnown) {
    return true;
  }

  const _messageKnownAt = messageKnownAt(state, router.selectors.accountId(state), 'AUTO_RENEW_OFF');
  return !moment(_messageKnownAt).isSame(moment(), 'day');
}

const trialNearExpireMessageAvailable = (state) => {
  const _status = status(state);
  if(!_status) {
    return false;
  }

  if(_status !== 'TRIALING') {
    return false;
  }

  const _trilEndsDays = trilEndsDays(state);
  const config = app.selectors.config(state);
  const trialExpiryNotificationBeforeDays = config?.trialExpiryNotificationBeforeDays;
  if(_trilEndsDays > trialExpiryNotificationBeforeDays) {
    return false;
  }

  const _messageKnown = messageKnown(state, router.selectors.accountId(state), 'TRIAL_NEAR_EXPIRE');
  if(!_messageKnown) {
    return true;
  }

  const _messageKnownAt = messageKnownAt(state, router.selectors.accountId(state), 'TRIAL_NEAR_EXPIRE');
  return !moment(_messageKnownAt).isSame(moment(), 'day');
}

const pastDueMessageAvailable = (state) => {
  const _status = status(state);
  if(!_status) {
    return false;
  }

  if(_status !== 'PAST_DUE') {
    return false;
  }

  const _messageKnown = messageKnown(state, router.selectors.accountId(state), 'PAST_DUE');
  return !_messageKnown;
}

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - alert message type
 *                   - Possible values: 'TRIAL_NEAR_EXPIRE', 'AUTO_RENEW_OFF', 'PAST_DUE'
 */
export const alertMessageType = (state) => {
  if(trialNearExpireMessageAvailable(state)) {
    return 'TRIAL_NEAR_EXPIRE';
  }

  if(autoRenewOffMessageAvailable(state)) {
    return 'AUTO_RENEW_OFF';
  }

  if(pastDueMessageAvailable(state)) {
    return 'PAST_DUE';
  }

  return '';
}
// ================================== END: Subscription Messages ==================================

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - last invoice details for given/current account.
 *  - @param { Number } amount - Amount of the invoice.
 *  - @param { String } currencySymbol - Currency of the invoice.
 */
export const lastInvoice = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.lastInvoice.data`),
  (lastInvoice) => lastInvoice || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the last invoice API call.
 */
export const lastInvoiceStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.lastInvoice.status`),
  (status) => status || 'IN_PROGRESS'
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of non-profit request API call.
 */
export const nonProfitRequestStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.nonProfitRequest.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { String } - Error for non-profit request API call.
 */
export const nonProfitRequestError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.nonProfitRequest.error`),
  (error) => error || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Object } - Non-profit request details for given/current account from Firestore.
 */
export const nonProfitRequest = createSelector(
  (state, accountId) => firestoreRedux.selectors.doc(state, 'non-profit-requests', `npr_${getIdWoPrefix({ id: accountId || router.selectors.accountId(state), prefix: 'acc_' })}`),
  (request) => request || {}
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Boolean } - given account is non-profit or not.
 */
export const nonProfitAccount = createSelector(
  (state, accountId) => coupon(state, accountId),
  (state, accountId) => app.selectors.config(state),
  (coupon, config) => {
    if(!config) {
      return false;
    }
    
    const nonProfitCoupon = get(config, 'nonProfitCoupon', '');
    return coupon && coupon === nonProfitCoupon || false;
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the request invoice.
 */
export const requestInvoiceStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.requestInvoice.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { String } - Error for request invoice.
 */
export const requestInvoiceError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.requestInvoice.error`),
  (error) => error || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Boolean } - Whether offline payment is enabled for given account.
 */
export const offlinePaymentEnabled = createSelector(
  (state, accountId) => firestoreRedux.selectors.doc(state, 'accounts', accountId || router.selectors.accountId(state)) || {},
  (account) => account?.offlinePaymentEnabled || false
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Enum } - Status of the update billing address API call.
 */
export const updateBillingAddressStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.updateBillingAddress.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { String } - Error for update billing address API call.
 */
export const updateBillingAddressError = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.updateBillingAddress.error`),
  (error) => error || ''
);

/**
 * @returns { Object } - request invoice.
 */
export const requestInvoice = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.requestInvoice.data`),
  (data) => data || {}
);

export const cancelSubscriptionStatus = createSelector(
  (state, accountId) => get(state, `subscription-v2.${accountId || router.selectors.accountId(state)}.cancelSubscription.status`),
  (status) => status || ''
);

/**
 * @param { Object } state - Redux state detail object
 * @returns { Boolean } - Whether enterprise plan is available at app level
 */
export const enterprisePlanPlaceholderAvailable = createSelector(
  (state) => app.selectors.config(state),
  (config) => {
    const _enterprisePlanPlaceholderAvailable = config?.enterprisePlanPlaceholderAvailable || false;
    return _enterprisePlanPlaceholderAvailable === true || _enterprisePlanPlaceholderAvailable === 'true' || false;
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { Number } - Minimum required seats for the account based on used seats and enterprise min seats.
 */
export const minRequiredSeats = createSelector(
  (state, accountId) => subscription(state, accountId || router.selectors.accountId(state)),
  (state, accountId) => firestoreRedux.selectors.doc(state, 'accounts', accountId || router.selectors.accountId(state)),
  (subscription, account) => {
    const usedSeats = subscription?.usedSeats || 0;
    const enterpriseMinSeats = account?.enterpriseMinSeats || 0;
    return Math.max(usedSeats, enterpriseMinSeats, 1); // At least 1 seat required
  }
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { Number } - Seats for the account based on subscription seats or used seats.
 */
export const seats = createSelector(
  (state, accountId) => subscription(state, accountId || router.selectors.accountId(state)),
  (subscription) => subscription?.seats || 0
);

/**
 * @param { Object } state - Redux state detail object
 * @param { String } accountId - Account ID.
 * @returns { Number } - Free seats for the account based on subscription seats and used seats.
 */
export const freeSeats = createSelector(
  (state, accountId) => subscription(state, accountId || router.selectors.accountId(state)),
  (subscription) => {
    const seats = subscription?.seats || 0;
    const usedSeats = subscription?.usedSeats || 0;
    return Math.max(seats - usedSeats, 0);
  }
);